import React from "react"
import * as React_ from "elm-ts/lib/React"
import * as M from "../Model/rentalsModel"
import { pipe, flow, identity } from "fp-ts/function"
import * as E from "fp-ts/Either"
import * as O from "fp-ts/Option"
import * as A from "fp-ts/Array"
import { boolean } from "fp-ts"
import { BrandSpinner, Icon, Modal } from "../../components"
import {
  CalendarWidget,
  Error,
  Pie,
  RentalsBreakdown,
  FAQModal,
} from "../components"
import { numberAbreviation } from "../../lib/format"
import dayjs from "dayjs"
import {
  Variant,
  BookingsByMonth,
  RentalsMonthlyBreakdown,
} from "@flypengine/myFlyp-lib"
import { ChevronRight } from "../../components/Icon"
import { PageStatus } from "../../lib/types"

export function rentalsView<msg>(
  config: M.Config<msg>
): (model: M.Model) => React_.Html<msg> {
  return model => dispatch => {
    return pipe(
      model,
      E.match(
        status => {
          switch (status) {
            case "Loading": {
              return (
                <div data-cy="dashboard-rentals-loader">
                  <BrandSpinner description="Loading deal's data" />
                </div>
              )
            }
            case "Error": {
              return (
                <Error
                  dataCy="dashboard-rentals-error"
                  message=" Data not found. If this doesn't look right please"
                />
              )
            }

            case "Not Authorized": {
              return (
                <Error
                  dataCy="dashboard-rentals-error-auth"
                  message="You are not authorized to see this content. If this doesn't look right please"
                />
              )
            }
            default: {
              return <></>
            }
          }
        },
        m => (
          <div className="layout-grid">
            <div className="layout-col">
              <h4 className="heading">Your Flyp Payment position</h4>
              {viewFinantialPosition(m.finantialPositionModel)(
                flow(config.wrapperMsg, dispatch)
              )}
              {viewFrequentQuestionsModel({
                contents: m.faqModel,
                updateContents: M.Msg.faqModal,
                updateModalContents: M.Msg.modalContents,
              })(flow(config.wrapperMsg, dispatch))}
            </div>
            <div className="layout-col">
              <h4 className="heading">Booking Income</h4>
              {viewBookingIncome(m.bookingIncomeModel)(
                flow(config.wrapperMsg, dispatch)
              )}
            </div>
            <div className="layout-col">
              <Popover
                title={<h4 className="heading">Property Performance</h4>}
                label="Total property performance from live date up to current month (after having removed unavailable blocks/days)"
                dataCy="dashboard-rentals-performance"
              />
              {viewPropertyPerformance(m.propertyPerformanceModel)(
                flow(config.wrapperMsg, dispatch)
              )}
            </div>
            {modalView(m)(flow(config.wrapperMsg, dispatch))}
          </div>
        )
      )
    )
  }
}

function viewFinantialPosition(
  contents: M.FinantialPosition
): React_.Html<M.Msg> {
  const outstanding = pipe(
    contents.outstanding,
    O.match(
      () => ({ percentage: 0, gross: { value: 0, name: O.none } }),
      ({ gross, percentage }) => ({
        gross: numberAbreviation(gross),
        percentage,
      })
    )
  )
  return _dispatch => (
    <div className="card bg-slate">
      <Popover
        title={<h5 className="heading text-white">Outstanding Flyp Payment</h5>}
        label="includes VAT"
        dataCy=""
        backgroundColor="bg-slate"
        textColor="text-white"
        textSize="text-md"
      />
      <h5
        className="heading text-[4rem] leading-none text-glass mt-1"
        data-cy="dashboard-rentals-fis-value"
      >
        £ {outstanding.gross.value}{" "}
        {O.getOrElse(() => "")(outstanding.gross.name)}
      </h5>
      <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[90%]">
        <Pie
          dataCy={`dashboard-rentals-fis-percentage-${outstanding.percentage}`}
          slices={[outstanding.percentage, 100 - outstanding.percentage]}
          colors={["var(--glass)", "var(--glass-light)"]}
          background={"var(--slate)"}
        />
      </div>
    </div>
  )
}

function viewBookingIncome(contents: M.BookingIncome): React_.Html<M.Msg> {
  return dispatch => {
    const nextBooking = pipe(
      contents.upcommingBooking,
      O.map(upcomming => ({
        label: dayjs(upcomming).format("MMM"),
        value: Number(dayjs(upcomming).format("DD")),
      }))
    )

    return (
      <>
        {contents.breakdown.length == 0 ? (
          <div className="card bg-stone-light">
            <div
              className="card flex flex-col h-full"
              data-cy="dashboard-rentals-breakdown-empty"
            >
              <div className="grow flex flex-col items-center  justify-center text-center">
                <h4 className="heading text-4xl text-brick mb-6">
                  Monthly breakdown
                </h4>
                <p>Nothing to show yet</p>
                <a
                  href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
                  className="button primary mt-6"
                >
                  Contact us
                </a>
              </div>
            </div>
          </div>
        ) : (
          <div className="card flex flex-col">
            <RentalsBreakdown
              items={contents.breakdown}
              onSelect={(currentIdx, id) => {
                {
                  dispatch(M.Msg.lastFocused(O.some(id)))
                  dispatch(
                    M.Msg.modalContents(
                      O.some(
                        M.Modal.MonthlyBreakdown({
                          breakdown: contents.breakdown,
                          slide: currentIdx,
                        })
                      )
                    )
                  )
                }
              }}
            />
          </div>
        )}
        <CalendarWidget
          title="Booking Calendar"
          label={pipe(
            nextBooking,
            O.match(
              () => "Stand by",
              ({ label }) => label
            )
          )}
          counter={pipe(
            nextBooking,
            O.match(
              () => 0,
              ({ value }) => value
            )
          )}
          showCalendarIcon={false}
          dataCy="dashboard-rentals-bookings"
          buttonLabel="Your next booking"
          onClickMsg={pipe(
            nextBooking,
            O.match(
              () => () => {},
              () => () => {
                const monthIdx = pipe(
                  contents.breakdown,
                  A.findIndex(s =>
                    dayjs(s.range.start).isSame(dayjs(), "month")
                  ),
                  O.match(() => 0, identity)
                )
                dispatch(
                  M.Msg.modalContents(
                    O.some(
                      M.Modal.MonthlyBreakdown({
                        breakdown: contents.breakdown,
                        slide: monthIdx,
                      })
                    )
                  )
                )
              }
            )
          )}
        />
      </>
    )
  }
}

function viewPropertyPerformance(
  contents: M.PropertyPerformance
): React_.Html<M.Msg> {
  const adr = pipe(
    contents.averageDailyRate,
    O.map(averageDailyRate => numberAbreviation(averageDailyRate))
  )
  const bookingIncome = pipe(
    contents.bookingIncome,
    O.map(bookingIncome => numberAbreviation(bookingIncome))
  )

  return _dispatch => (
    <div className="row-span-2 flex flex-col gap-6">
      <div className="grow card flex flex-col">
        <Popover
          title={<p>Booking Income to date</p>}
          label="Booking income collected from guests, up to current month"
          dataCy="dashboard-rentals-performance-income"
        />
        <div className="grow flex flex-row items-end">
          {pipe(
            bookingIncome,
            O.match(
              () => (
                <p
                  className="grow text-sm italic"
                  data-cy="dashboard-rentals-performance-income-empty"
                >
                  Check back soon, this is calculated 30 days post launch
                </p>
              ),
              ({ name, value }) => (
                <>
                  <p
                    className="grow heading text-5xl"
                    data-cy="dashboard-rentals-performance-income-label"
                  >
                    £ {value} {O.getOrElse(() => "")(name)}
                  </p>
                </>
              )
            )
          )}
        </div>
      </div>
      <div className="grow card flex flex-col">
        <Popover
          title={<p>Occupancy rate</p>}
          label="booked nights / available nights"
          dataCy="dashboard-rentals-performance-occupancy"
        />
        <div className="grow flex flex-row items-end">
          {pipe(
            contents.occupancy,
            O.match(
              () => (
                <p
                  className=" grow text-sm italic"
                  data-cy="dashboard-rentals-performance-occupancy-empty"
                >
                  Check back soon, this is calculated 30 days post launch
                </p>
              ),
              occupancy => (
                <>
                  <p
                    className=" grow heading text-5xl"
                    data-cy="dashboard-rentals-performance-occupancy-label"
                  >
                    {occupancy} %
                  </p>
                </>
              )
            )
          )}
        </div>
      </div>
      <div className="grow card flex flex-col">
        <Popover
          title={<p>ADR to date</p>}
          label="Avg. nightly rate of bookings"
          dataCy="dashboard-rentals-performance-adr"
        />
        <div className="grow flex flex-row items-end">
          {pipe(
            adr,
            O.match(
              () => (
                <p
                  className=" grow text-sm italic"
                  data-cy="dashboard-rentals-performance-adr-empty"
                >
                  Check back soon, this is calculated 30 days post launch
                </p>
              ),
              ({ name, value }) => (
                <p
                  className=" grow heading text-5xl"
                  data-cy="dashboard-rentals-performance-adr-label"
                >
                  £ {value} {O.getOrElse(() => "")(name)}
                </p>
              )
            )
          )}
        </div>
      </div>
      <div className="grow card flex flex-col">
        <div data-cy="dashboard-rentals-performance-yield-title">
          Yield to date
        </div>
        <div className="grow flex flex-row items-end">
          <div className="grow flex flex-row items-end">
            {pipe(
              contents.yieldToDate,
              O.match(
                () => (
                  <p
                    className=" grow text-sm italic"
                    data-cy="dashboard-rentals-performance-yield-empty"
                  >
                    Check back soon, this is calculated 30 days post launch
                  </p>
                ),
                value => (
                  <p
                    className=" grow heading text-5xl"
                    data-cy="dashboard-rentals-performance-yield-label"
                  >
                    {value} %
                  </p>
                )
              )
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
function viewFrequentQuestionsModel({
  contents,
  updateContents,
  updateModalContents,
}: {
  contents: M.FrequentQuestionsModel
  updateContents: (m: M.FrequentQuestionsModel) => M.Msg
  updateModalContents: (m: O.Option<M.Modal>) => M.Msg
}): React_.Html<M.Msg> {
  return dispatch => (
    <div className="card md:px-8 pb-8 flex flex-col">
      {pipe(
        contents.faqs,
        A.mapWithIndex((idx, faq) =>
          idx === contents.index ? (
            <div key={idx}>
              <div className="flex justify-between">
                <h4 className="heading">FAQ</h4>
                <button
                  data-cy="dashboard-rental-faq-btn"
                  onClick={() =>
                    dispatch(
                      updateModalContents(
                        O.some(
                          M.Modal.FAQ({
                            index: idx,
                            faqs: contents.faqs,
                          })
                        )
                      )
                    )
                  }
                >
                  <Icon.Expand className="text-white bg-slate w-5 h-5 rounded-2xl" />
                </button>
              </div>
              <div className="status-shape grow px-4 ">
                <h3
                  className="heading text-2xl md:text-3xl mb-1"
                  data-cy={`dashboard-rentals-faq-${idx}`}
                >
                  {faq.question}
                </h3>
                <div className="h-[70px] relative">
                  <span className="text-md leading-4 block">
                    {faq.shortAnswer}
                  </span>
                  <button
                    className="underline font-bold absolute -bottom-2"
                    data-cy="dashboard-rental-faq-expand"
                    onClick={() =>
                      dispatch(
                        updateModalContents(
                          O.some(
                            M.Modal.FAQ({
                              index: idx,
                              faqs: contents.faqs,
                            })
                          )
                        )
                      )
                    }
                  >
                    Read more
                  </button>
                </div>
              </div>
              <div
                className=" mb-4 shrink-0 status-tic bg-glass-light  "
                style={
                  {
                    "--n": idx,
                    "--total": contents.faqs.length,
                  } as React.CSSProperties
                }
              ></div>
            </div>
          ) : (
            <React.Fragment key={idx} />
          )
        )
      )}

      <div className=" shrink-0  h-4 flex gap-3 relative ">
        {pipe(
          contents.faqs,
          A.mapWithIndex((idx, _faq) => (
            <button
              key={idx}
              onClick={() =>
                pipe({ ...contents, index: idx }, updateContents, dispatch)
              }
              className={
                "grow bg-glass-light rounded-xl" +
                (idx === contents.index ? "" : " opacity-50")
              }
              data-cy={`dashboard-rentals-faq-${idx}-nav`}
            ></button>
          ))
        )}
      </div>
    </div>
  )
}

function modalView({
  modal,
  bookingsByMonth,
}: {
  modal: O.Option<M.Modal>
  bookingsByMonth: E.Either<PageStatus, BookingsByMonth>
}): React_.Html<M.Msg> {
  return Modal({
    isOpen: pipe(modal, O.isSome),
    closeMsg: M.Msg.modalContents(O.none),
    containerStyles: "card rounded-[14px] bg-stone",
    body: dispatch =>
      pipe(
        modal,
        O.match(
          () => (
            <div
              data-cy={`dashboard-rentals-modal-error`}
              className="w-full h-[700px] flex flex-col items-center justify-center"
            >
              <h1 className="mb-8">Something went wrong...</h1>
              <p className="text-lg">Please refresh the page and try again.</p>
            </div>
          ),
          m =>
            pipe(
              m,
              Variant.caseOf({
                FAQ: contents => <FAQModal {...contents} />,
                MonthlyBreakdown: bookingModal =>
                  monthlyBreakdownModal({ bookingModal, bookingsByMonth })(
                    dispatch
                  ),
              })
            )
        )
      ),
  })
}

function monthlyBreakdownModal({
  bookingModal: { breakdown, slide },
  bookingsByMonth,
}: {
  bookingModal: M.BookingsModal
  bookingsByMonth: E.Either<PageStatus, BookingsByMonth>
}): React_.Html<M.Msg> {
  const rangeStart = pipe(
    breakdown[slide],
    O.fromNullable,
    O.map(({ range }) => range.start)
  )

  return dispatch => (
    <div className="w-full min-h-[700px]">
      <div className="flex items-center justify-between md:justify-center md:gap-10 my-8">
        <button
          className="flex-basis button-icon rotate-180 disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000]"
          disabled={slide === 0}
          onClick={() => {
            dispatch(
              M.Msg.modalContents(
                O.some(
                  M.Modal.MonthlyBreakdown({
                    slide: slide - 1,
                    breakdown,
                  })
                )
              )
            )
          }}
        >
          <ChevronRight />
        </button>
        <h1
          className="md:w-[350px] text-center capitalize"
          data-cy={`dashboard-rentals-modal-${slide}`}
        >
          {pipe(
            rangeStart,
            O.match(
              () => "",
              date => dayjs(date).format("MMMM")
            )
          )}
          <span className=" ml-6 hidden xs:inline">
            {pipe(
              rangeStart,
              O.match(
                () => "",
                date => dayjs(date).format("YYYY")
              )
            )}
          </span>
        </h1>
        <button
          className="flex-basis button-icon disabled:[--icon-fill:#fff] disabled:[--icon-stroke:#000]"
          disabled={slide === breakdown.length - 1}
          onClick={() => {
            dispatch(
              M.Msg.modalContents(
                O.some(
                  M.Modal.MonthlyBreakdown({
                    slide: slide + 1,
                    breakdown,
                  })
                )
              )
            )
          }}
        >
          <ChevronRight />
        </button>
      </div>

      {pipe(
        rangeStart,
        O.exists(date => !dayjs(date).isBefore(dayjs().startOf("month"))),
        boolean.match(
          () => <></>,
          () => (
            <div className="text-sm text-center mb-4 text-gray-600">
              {"*Future booking values may change."}
            </div>
          )
        )
      )}

      <div>
        {pipe(
          bookingsByMonth,
          E.match(
            status => {
              switch (status) {
                case "Loading": {
                  return (
                    <div data-cy={`dashboard-rentals-month-${slide}-loader`}>
                      <BrandSpinner description={`Loading booking data`} />
                    </div>
                  )
                }
                case "Error": {
                  return (
                    <div
                      data-cy={`dashboard-rentals-month-${slide}-error`}
                      className="h-[700px] flex flex-col items-center justify-center"
                    >
                      <div>
                        <h1 className="mb-8">Something went wrong...</h1>
                        <p className="text-lg">
                          Data not found. If this doesn't look right please{" "}
                          <a
                            className="underline text-brick"
                            href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
                          >
                            get in touch
                          </a>
                        </p>
                      </div>
                    </div>
                  )
                }

                default: {
                  return <></>
                }
              }
            },
            rentals =>
              pipe(
                rangeStart,
                O.chain(start =>
                  pipe(
                    rentals,
                    A.findIndex(r =>
                      dayjs(r.range_start).isSame(dayjs(start), "month")
                    ),
                    O.chain(idx => pipe(rentals[idx], O.fromNullable))
                  )
                ),
                O.match(
                  () => (
                    <div
                      data-cy={`dashboard-rentals-month-${slide}-error`}
                      className="h-[700px] flex flex-col items-center justify-center"
                    >
                      <div>
                        <h1 className="mb-8">Something went wrong...</h1>
                        <p className="text-lg">
                          Data not found. If this doesn't look right please{" "}
                          <a
                            className="underline text-brick"
                            href="https://api.whatsapp.com/send/?phone=447723477585&text&type=phone_number&app_absent=0"
                          >
                            get in touch
                          </a>
                        </p>
                      </div>
                    </div>
                  ),
                  ({ bookingsData }) =>
                    monthlyBreakdownBookingData(bookingsData)
                )
              )
          )
        )}
      </div>
    </div>
  )
}

function monthlyBreakdownBookingData({
  bookings,
  adr,
  occupancy,
  totalIncome,
}: RentalsMonthlyBreakdown): JSX.Element {
  const income = numberAbreviation(totalIncome)
  const averageDailyRate = numberAbreviation(adr)

  return (
    <div className="grid md:grid-cols-2 gap-6">
      <div className="flex flex-col gap-6">
        <div className="grow card">
          <h2 className="heading mb-6 ">Booking data</h2>
          <table className=" block w-full">
            <thead className=" block font-sans">
              <tr className=" rounded-lg py-2 px-5 bg-concrete flex flex-row justify-between mb-3">
                <th>Check in</th>
                <th>Check out</th>
                <th>Booking Value</th>
              </tr>
            </thead>
            <tbody className=" block font-mono h-[280px] overflow-auto">
              {bookings.map((booking, b) => (
                <tr
                  key={b}
                  className=" rounded-lg py-2 px-5 bg-concrete flex flex-row justify-between mb-3"
                >
                  <th>{dayjs(booking.checkIn).format("DD/MM")}</th>
                  <th>{dayjs(booking.checkOut).format("DD/MM")}</th>
                  <th>£{booking.netIncome.toFixed(2)}</th>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="card bg-slate text-white min-h-[160px] ">
          <p className="text-xl">
            Projections - You will receive your statement at the end of the
            month
          </p>
        </div>
      </div>
      <div className="grid grid-rows-[1fr_1fr_250px] gap-6">
        <div className="grow card flex flex-col">
          <Popover
            title={<p>Booking Income</p>}
            label="Booking income collected from guests"
            dataCy="dashboard-rentals-performance-month-income"
          />
          <div className="grow flex flex-col sm:flex-row items-end ">
            <p className=" grow heading text-5xl">
              <span>
                £ {income.value}
                <span className="font-size: 0.8em; margin-left: 0.2em;">
                  {O.getOrElse(() => "")(income.name)}
                </span>
              </span>
            </p>
          </div>
        </div>
        <div className="grow card flex flex-col">
          <Popover
            title={<p>ADR</p>}
            label="Avg. nightly rate of bookings"
            dataCy="dashboard-rentals-performance-month-adr"
          />
          <div className="grow flex flex-col sm:flex-row items-end ">
            <p className=" grow heading text-5xl">
              <span>
                £ {Math.round(averageDailyRate.value)}
                {O.getOrElse(() => "")(averageDailyRate.name)}
              </span>
            </p>
          </div>
        </div>

        <div className=" relative card bg-slate  ">
          <h5 className="heading text-white ">Occupancy</h5>
          <h5 className="heading text-[2.5rem] leading-none text-glass mt-1 ">
            {Math.round(occupancy)}%
          </h5>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[80%]">
            <Pie
              dataCy=""
              slices={[occupancy, 100 - occupancy]}
              colors={["var(--glass)", "var(--glass-light)"]}
              background={"var(--slate)"}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

const Popover = ({
  title,
  label,
  dataCy,
  backgroundColor = "bg-lawn",
  textColor = "text-black",
  textSize = "text-xs",
}: {
  title: React.ReactNode
  label: string
  dataCy: string
  backgroundColor?: string
  textColor?: string
  textSize?: string
}) => {
  const [isVisible, setIsVisible] = React.useState(false)

  return (
    <div className="relative" data-cy={`${dataCy}-title`}>
      {title}
      <div className="group absolute top-3 right-2 translate-x-[50%] translate-y-[-50%]">
        <div
          className={`w-4 h-4 grid place-items-center ${backgroundColor} cursor-help`}
          onClick={() => setIsVisible(!isVisible)}
          onMouseEnter={() => setIsVisible(true)}
          onMouseLeave={() => setIsVisible(false)}
        >
          <span className={`font-mono leading-none ${textSize} ${textColor}`}>
            i
          </span>
        </div>
        <div className={`${isVisible ? "block" : "hidden"}`}>
          <p
            className={`absolute top-2 right-2 translate-x-[19px] -translate-y-full marker ${backgroundColor} ${textColor} ${textSize} w-max max-w-[300px] p-2 rounded shadow-lg z-10`}
            data-cy={`${dataCy}-info`}
          >
            {label}
          </p>
        </div>
      </div>
    </div>
  )
}
